import Phaser from "phaser";
import type { PolarityAdminApi } from "./game/admin";
import {
  createPolarityComplexityApi,
  type PolarityComplexityApi,
} from "./game/complexity";
import { GameScene } from "./game/GameScene";
import { GAME_HEIGHT, GAME_WIDTH } from "./game/config";
import { createMobileUiController } from "./game/mobileUi";
import { initializePolarityTrace } from "./game/trace";
import "./styles.css";

declare global {
  interface Window {
    __polarityAdminEnabled?: boolean;
    polarityComplexity?: PolarityComplexityApi;
    __polarityDebugHooksEnabled?: boolean;
    advanceTime?: (ms: number) => Promise<void> | void;
    polarityAdmin?: PolarityAdminApi;
    render_game_to_text?: () => string;
  }
}

const searchParams = new URLSearchParams(window.location.search);

window.__polarityAdminEnabled = searchParams.get("admin") === "1";
window.__polarityDebugHooksEnabled =
  import.meta.env.DEV || searchParams.get("debugHooks") === "1";
initializePolarityTrace(searchParams.get("trace") === "1");
window.polarityComplexity =
  window.__polarityAdminEnabled || window.__polarityDebugHooksEnabled
    ? createPolarityComplexityApi()
    : undefined;

const appElement = document.getElementById("app");
if (!appElement) {
  throw new Error("App root element was not found.");
}

const mobileUi = createMobileUiController(appElement);

const VIEWPORT_RESIZE_SETTLE_DELAYS_MS = [60, 180, 360, 720] as const;

const game = new Phaser.Game({
  type: Phaser.CANVAS,
  parent: appElement,
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  backgroundColor: "#061522",
  scale: {
    parent: "app",
    mode: Phaser.Scale.RESIZE,
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
  },
  scene: [new GameScene({ mobileUi })],
});

const viewportResizeSync = createViewportResizeSync(appElement, game);

window.addEventListener("beforeunload", () => {
  window.polarityComplexity = undefined;
  viewportResizeSync.destroy();
  mobileUi.destroy();
  game.destroy(true);
});

interface ViewportResizeSync {
  destroy(): void;
}

function createViewportResizeSync(root: HTMLElement, game: Phaser.Game): ViewportResizeSync {
  let frameId: number | null = null;
  const timerIds: number[] = [];
  const visualViewport = window.visualViewport;

  const syncNow = () => {
    frameId = null;
    const { width, height } = readViewportSize(root);

    root.style.width = `${width}px`;
    root.style.height = `${height}px`;

    const canvas = game.canvas;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    if (
      Math.abs(game.scale.gameSize.width - width) > 1 ||
      Math.abs(game.scale.gameSize.height - height) > 1
    ) {
      game.scale.resize(width, height);
    }
  };

  const scheduleFrameSync = () => {
    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
    }

    frameId = window.requestAnimationFrame(syncNow);
  };

  const clearTimers = () => {
    while (timerIds.length > 0) {
      const timerId = timerIds.pop();
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }
    }
  };

  const scheduleSettledSync = () => {
    clearTimers();
    scheduleFrameSync();

    for (const delay of VIEWPORT_RESIZE_SETTLE_DELAYS_MS) {
      timerIds.push(window.setTimeout(scheduleFrameSync, delay));
    }
  };

  window.addEventListener("resize", scheduleSettledSync, { passive: true });
  window.addEventListener("orientationchange", scheduleSettledSync);
  visualViewport?.addEventListener("resize", scheduleSettledSync, { passive: true });
  visualViewport?.addEventListener("scroll", scheduleSettledSync, { passive: true });
  scheduleSettledSync();

  return {
    destroy(): void {
      clearTimers();
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener("resize", scheduleSettledSync);
      window.removeEventListener("orientationchange", scheduleSettledSync);
      visualViewport?.removeEventListener("resize", scheduleSettledSync);
      visualViewport?.removeEventListener("scroll", scheduleSettledSync);
    },
  };
}

function readViewportSize(root: HTMLElement): { width: number; height: number } {
  const visualViewport = window.visualViewport;
  const rootBounds = root.getBoundingClientRect();
  const width = Math.round(
    positiveNumber(visualViewport?.width) ??
      positiveNumber(rootBounds.width) ??
      positiveNumber(window.innerWidth) ??
      GAME_WIDTH,
  );
  const height = Math.round(
    positiveNumber(visualViewport?.height) ??
      positiveNumber(rootBounds.height) ??
      positiveNumber(window.innerHeight) ??
      GAME_HEIGHT,
  );

  return {
    width: Math.max(320, width),
    height: Math.max(240, height),
  };
}

function positiveNumber(value: number | undefined): number | undefined {
  return value !== undefined && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}
